import Icon from "./Icon.jsx";
import { SKILL_TREE } from "../data/curriculum.js";

export default function SkillMap({ track, onStartLesson }) {
  return (
    <div className="page fade-up">
      <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
        <aside style={{ width: 280, position: "sticky", top: 100, flexShrink: 0 }}>
          <div className="card" style={{ padding: 24 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: "var(--terracotta)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-serif)",
                fontSize: 32,
                marginBottom: 14,
              }}
            >
              ✳
            </div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Your track</div>
            <h2 className="serif" style={{ fontSize: 30, margin: "0 0 8px", letterSpacing: "-0.015em" }}>
              {track?.title || "Prompt Engineering"}
            </h2>
            <div style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20 }}>
              {track?.subtitle || "From zero to power-user"}
            </div>

            <div className="eyebrow" style={{ marginBottom: 6 }}>Progress</div>
            <div className="progress" style={{ marginBottom: 10 }}>
              <span style={{ width: "18%" }} />
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 18 }}>
              2 of 13 nodes · 45 XP earned
            </div>

            <div className="eyebrow" style={{ marginBottom: 10 }}>Recent badges</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { c: "var(--terracotta)", g: "🌱" },
                { c: "var(--gold)", g: "⚡" },
                { c: "var(--gem)", g: "✳" },
              ].map((b, i) => (
                <div
                  key={i}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    background: b.c,
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 18,
                    boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.2)",
                  }}
                >
                  {b.g}
                </div>
              ))}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  background: "var(--cream-3)",
                  color: "var(--ink-4)",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 16,
                  border: "1px dashed var(--line-2)",
                }}
              >
                +3
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20, marginTop: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Leaderboard · this week</div>
            {[
              { n: "Mina", xp: 1240, you: false },
              { n: "Aarav", xp: 980, you: false },
              { n: "You", xp: 45, you: true },
              { n: "Jules", xp: 40, you: false },
            ].map((r, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: i < 3 ? "1px solid var(--line)" : "none",
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    display: "grid",
                    placeItems: "center",
                    background:
                      i === 0 ? "var(--gold)" : i === 1 ? "var(--cream-3)" : i === 2 ? "var(--terracotta-wash)" : "var(--cream-2)",
                    color: i < 3 ? "var(--ink)" : "var(--ink-3)",
                  }}
                >
                  {i + 1}
                </div>
                <div
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: r.you ? 600 : 400,
                    color: r.you ? "var(--terracotta)" : "var(--ink)",
                  }}
                >
                  {r.n}
                </div>
                <div className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {r.xp} XP
                </div>
              </div>
            ))}
          </div>
        </aside>

        <div style={{ flex: 1, minWidth: 320 }}>
          <div style={{ marginBottom: 28 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Skill map</div>
            <h1
              className="serif"
              style={{
                fontSize: 52,
                margin: 0,
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
              }}
            >
              Your path to{" "}
              <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>mastery</span>.
            </h1>
            <p style={{ color: "var(--ink-3)", fontSize: 16, maxWidth: 560, marginTop: 12 }}>
              Each node is a short lesson or a challenge. Finish the current one to unlock the next.
            </p>
          </div>

          {SKILL_TREE.map((unit) => (
            <UnitBlock key={unit.unit} unit={unit} onStartLesson={onStartLesson} />
          ))}
        </div>
      </div>
    </div>
  );
}

function UnitBlock({ unit, onStartLesson }) {
  return (
    <div style={{ marginBottom: 44 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          padding: "12px 18px",
          background: "var(--cream-2)",
          border: "1px solid var(--line)",
          borderRadius: 12,
          marginBottom: 18,
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--terracotta)",
            fontWeight: 600,
            letterSpacing: "0.14em",
          }}
        >
          UNIT {String(unit.unit).padStart(2, "0")}
        </div>
        <div className="serif" style={{ fontSize: 22, letterSpacing: "-0.01em" }}>
          {unit.title}
        </div>
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{unit.caption}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "relative" }}>
        {unit.nodes.map((n, i) => (
          <Node
            key={n.id}
            node={n}
            side={i % 2 === 0 ? "left" : "right"}
            onStartLesson={onStartLesson}
          />
        ))}
      </div>
    </div>
  );
}

function Node({ node, side, onStartLesson }) {
  const locked = node.status === "locked";
  const current = node.status === "current";
  const done = node.status === "done";

  const kindMeta =
    {
      lesson: { label: "Lesson", icon: "book", bg: "var(--cream-2)" },
      practice: { label: "Practice", icon: "target", bg: "var(--moss-wash)" },
      checkpoint: { label: "Checkpoint", icon: "sparkle", bg: "var(--terracotta-wash)" },
      boss: { label: "Boss", icon: "skull", bg: "#EDD6CE" },
      final: { label: "Final", icon: "trophy", bg: "var(--gold-wash)" },
    }[node.kind] || { label: "Node", icon: "book", bg: "var(--cream-2)" };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: side === "left" ? "flex-start" : "flex-end",
        paddingLeft: side === "left" ? 0 : "20%",
        paddingRight: side === "left" ? "20%" : 0,
      }}
    >
      <button
        disabled={locked}
        onClick={() => current && onStartLesson(node)}
        className="card"
        style={{
          width: "100%",
          maxWidth: 560,
          padding: 18,
          display: "flex",
          alignItems: "center",
          gap: 16,
          background: done ? "var(--cream-2)" : current ? kindMeta.bg : "#FBF7F0",
          opacity: locked ? 0.55 : 1,
          cursor: current ? "pointer" : "default",
          textAlign: "left",
          border: current ? "2px solid var(--terracotta)" : "1px solid var(--line)",
          boxShadow: current ? "0 12px 28px -14px rgba(201,100,66,0.5)" : "var(--shadow-sm)",
          transition: "transform 120ms ease",
          animation: current ? "fade-up 400ms ease both" : "none",
        }}
        onMouseEnter={(e) => {
          if (current) e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "";
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            flexShrink: 0,
            background: done
              ? "var(--moss)"
              : current
              ? "var(--terracotta)"
              : locked
              ? "var(--cream-3)"
              : "var(--ink)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            boxShadow: "inset 0 -3px 0 rgba(0,0,0,0.18)",
            position: "relative",
          }}
        >
          {locked ? (
            <Icon name="lock" size={20} color="var(--ink-4)" />
          ) : done ? (
            <Icon name="check" size={22} color="#fff" strokeWidth={2.4} />
          ) : (
            <span style={{ fontFamily: "var(--font-serif)", fontSize: 24 }}>
              {node.kind === "checkpoint" ? "✳" : node.kind === "boss" ? "✦" : node.kind === "final" ? "★" : "◆"}
            </span>
          )}
          {current && (
            <span
              style={{
                position: "absolute",
                inset: -6,
                borderRadius: 18,
                border: "2px solid var(--terracotta)",
                opacity: 0.4,
                animation: "pulse-soft 1.8s ease-in-out infinite",
              }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              {kindMeta.label}
            </span>
            <span className="chip" style={{ padding: "2px 8px", fontSize: 11, background: "transparent" }}>
              <Icon name="bolt" size={11} /> {node.xp} XP
            </span>
          </div>
          <div style={{ fontSize: 17, fontWeight: 500, letterSpacing: "-0.005em" }}>{node.title}</div>
        </div>
        {current && (
          <div
            style={{
              color: "var(--terracotta)",
              fontWeight: 600,
              fontSize: 14,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              paddingRight: 6,
            }}
          >
            Start <Icon name="arrow-right" size={14} color="var(--terracotta)" />
          </div>
        )}
        {done && (
          <div className="mono" style={{ fontSize: 12, color: "var(--moss)", fontWeight: 600 }}>
            ✓ Done
          </div>
        )}
      </button>
    </div>
  );
}
